import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { 
  Film, Clock, CheckCircle, Banknote, Play, FileText,
  Mail, Upload, Download, Loader2, ChevronRight, FolderOpen,
  AlertCircle, CreditCard, Plus, Eye, User, Phone, Building, MapPin, Edit2, Save, FileVideo, X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusSteps = [
  { id: 'soumission', label: 'Soumission', icon: FileText, color: 'bg-gray-500' },
  { id: 'evaluation', label: 'Évaluation', icon: Clock, color: 'bg-amber-500' },
  { id: 'levee', label: 'En cours de levée', icon: Banknote, color: 'bg-blue-500' },
  { id: 'virement', label: 'Virement émis', icon: CreditCard, color: 'bg-emerald-500' },
  { id: 'production', label: 'Production', icon: Film, color: 'bg-purple-500' },
  { id: 'diffusion', label: 'Diffusion', icon: Play, color: 'bg-green-500' },
];

const ProducerSpace = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [producer, setProducer] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [documents, setDocuments] = useState({});
  const [ribDocument, setRibDocument] = useState(null);
  const [pitchDocument, setPitchDocument] = useState(null);
  const [kbisDocument, setKbisDocument] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectCategory, setNewProjectCategory] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    company: '',
    address: '',
    director: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const categories = [
    'Court métrage fiction',
    'Court métrage documentaire',
    'Long métrage fiction',
    'Long métrage documentaire',
    'Série',
    'Animation',
    'Autre'
  ];

  const fetchProducerData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/producteur/${token}`);
      
      if (response.data.error) {
        setError(response.data.error);
        setIsLoading(false);
        return;
      }
      
      const producerData = response.data.producer;
      setProducer(producerData);
      setProjects(response.data.projects || []);
      
      // Initialize profile data from producer
      setProfileData({
        name: producerData?.name || '',
        phone: producerData?.phone || '',
        company: producerData?.company || '',
        address: producerData?.address || '',
        director: producerData?.director || ''
      });
      
      // Fetch documents for all projects
      const docsMap = {};
      let foundRib = null;
      let foundPitch = null;
      let foundKbis = null;
      
      for (const project of response.data.projects || []) {
        const docsRes = await axios.get(`${BACKEND_URL}/api/documents/${project.id}`);
        docsMap[project.id] = docsRes.data;
        
        // Check for specific document types
        for (const doc of docsRes.data) {
          if (doc.documentType === 'RIB' && !foundRib) foundRib = doc;
          if (doc.documentType === 'Pitch' && !foundPitch) foundPitch = doc;
          if (doc.documentType === 'KBIS' && !foundKbis) foundKbis = doc;
        }
      }
      
      setDocuments(docsMap);
      setRibDocument(foundRib);
      setPitchDocument(foundPitch);
      setKbisDocument(foundKbis);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching producer data:', err);
      setError('Impossible de charger vos données');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchProducerData();
    }
  }, [token]);

  const createProject = async () => {
    if (!newProjectTitle || !producer?.email) return;
    setCreatingProject(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/producer/create-project`, {
        email: producer.email,
        projectTitle: newProjectTitle,
        category: newProjectCategory || 'Non défini'
      });
      
      if (response.data.success) {
        await fetchProducerData();
        setNewProjectTitle('');
        setNewProjectCategory('');
        setShowCreateProject(false);
      }
    } catch (err) {
      console.error('Error creating project:', err);
    } finally {
      setCreatingProject(false);
    }
  };

  const handleDocumentUpload = async (file, documentType) => {
    if (!file || projects.length === 0) return;
    
    setUploadingDoc(documentType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projects[0].id);
      formData.append('documentType', documentType);
      formData.append('uploadedBy', 'producer');
      
      await axios.post(`${BACKEND_URL}/api/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      await fetchProducerData();
    } catch (err) {
      console.error(`Error uploading ${documentType}:`, err);
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDownload = (docId) => {
    window.open(`${BACKEND_URL}/api/documents/download/${docId}`, '_blank');
  };

  const saveProfile = async () => {
    if (!producer?.email) return;
    setSavingProfile(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/producer/profile`, {
        email: producer.email,
        ...profileData
      });
      setEditingProfile(false);
      await fetchProducerData();
    } catch (err) {
      console.error('Error saving profile:', err);
    } finally {
      setSavingProfile(false);
    }
  };

  const getStatusIndex = (status) => {
    const index = statusSteps.findIndex(s => s.id === status);
    return index >= 0 ? index : 0;
  };

  const getStatusInfo = (status) => {
    return statusSteps.find(s => s.id === status) || statusSteps[0];
  };

  const getProjectDocCount = (projectId) => {
    const docs = documents[projectId] || [];
    return docs.filter(d => d.uploadedBy !== 'admin' && !['RIB', 'Pitch', 'KBIS'].includes(d.documentType)).length;
  };

  const getAdminDocCount = (projectId) => {
    const docs = documents[projectId] || [];
    return docs.filter(d => d.uploadedBy === 'admin').length;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={48} className="text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="pt-32 pb-20 px-4 flex items-center justify-center">
          <div className="glass rounded-2xl p-10 text-center max-w-md">
            <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-white mb-2">Accès non autorisé</h2>
            <p className="text-[#a1a1aa]">{error}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      <main className="pt-28 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <p className="text-[#D4AF37] text-sm tracking-widest mb-2">ESPACE PRODUCTEUR</p>
            <h1 className="text-3xl md:text-4xl font-light text-white mb-2">
              Bienvenue{profileData.name ? `, ${profileData.name}` : ''}
            </h1>
            <p className="text-[#a1a1aa]">{producer?.email}</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Profile Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass rounded-2xl p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <User size={18} className="text-[#D4AF37]" />
                    Fiche Contact
                  </h3>
                  <button
                    onClick={() => setEditingProfile(!editingProfile)}
                    className="text-[#D4AF37] hover:text-[#F3E5AB] transition-colors"
                  >
                    {editingProfile ? <X size={18} /> : <Edit2 size={18} />}
                  </button>
                </div>

                {editingProfile ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[#71717a] text-sm mb-1">Nom / Prénom</label>
                        <input
                          type="text"
                          value={profileData.name}
                          onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                          placeholder="Jean Dupont"
                          className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                        />
                      </div>
                      <div>
                        <label className="block text-[#71717a] text-sm mb-1">Téléphone</label>
                        <input
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                          placeholder="06 12 34 56 78"
                          className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                        />
                      </div>
                      <div>
                        <label className="block text-[#71717a] text-sm mb-1">Société</label>
                        <input
                          type="text"
                          value={profileData.company}
                          onChange={(e) => setProfileData({...profileData, company: e.target.value})}
                          placeholder="Ma Production SARL"
                          className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                        />
                      </div>
                      <div>
                        <label className="block text-[#71717a] text-sm mb-1">Gérant</label>
                        <input
                          type="text"
                          value={profileData.director}
                          onChange={(e) => setProfileData({...profileData, director: e.target.value})}
                          placeholder="Nom du gérant"
                          className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-[#71717a] text-sm mb-1">Adresse</label>
                        <input
                          type="text"
                          value={profileData.address}
                          onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                          placeholder="123 rue du Cinéma, 75001 Paris"
                          className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                        />
                      </div>
                    </div>
                    <button
                      onClick={saveProfile}
                      disabled={savingProfile}
                      className="w-full py-3 bg-[#D4AF37] text-[#050505] rounded-lg font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {savingProfile ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <>
                          <Save size={18} />
                          Enregistrer
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-[#71717a]" />
                      <span className="text-[#a1a1aa]">{profileData.name || 'Non renseigné'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-[#71717a]" />
                      <span className="text-[#a1a1aa]">{profileData.phone || 'Non renseigné'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Building size={14} className="text-[#71717a]" />
                      <span className="text-[#a1a1aa]">{profileData.company || 'Non renseigné'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-[#71717a]" />
                      <span className="text-[#a1a1aa]">Gérant: {profileData.director || 'Non renseigné'}</span>
                    </div>
                    <div className="flex items-center gap-2 col-span-2">
                      <MapPin size={14} className="text-[#71717a]" />
                      <span className="text-[#a1a1aa]">{profileData.address || 'Non renseigné'}</span>
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Projects List */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl text-white font-medium">
                  Mes projets ({projects.length})
                </h2>
                <button
                  onClick={() => setShowCreateProject(!showCreateProject)}
                  className="flex items-center gap-2 text-[#D4AF37] hover:text-[#F3E5AB] text-sm transition-colors"
                >
                  <Plus size={16} />
                  Nouveau projet
                </button>
              </div>

              {/* Create Project Form */}
              {showCreateProject && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass rounded-2xl p-6"
                >
                  <h3 className="text-white font-medium mb-4">Créer un projet</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Titre du projet *</label>
                      <input
                        type="text"
                        value={newProjectTitle}
                        onChange={(e) => setNewProjectTitle(e.target.value)}
                        placeholder="Ex: Le Dernier Souffle"
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Catégorie</label>
                      <select
                        value={newProjectCategory}
                        onChange={(e) => setNewProjectCategory(e.target.value)}
                        className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#D4AF37]/50"
                      >
                        <option value="" className="bg-[#1a1a1a] text-white">Sélectionner une catégorie</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat} className="bg-[#1a1a1a] text-white">{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={createProject}
                        disabled={!newProjectTitle || creatingProject}
                        className="flex-1 py-3 bg-[#D4AF37] text-[#050505] rounded-lg font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {creatingProject ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <>
                            <Plus size={18} />
                            Créer le projet
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowCreateProject(false)}
                        className="px-4 py-3 bg-white/5 text-white rounded-lg hover:bg-white/10 transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {projects.length === 0 && !showCreateProject ? (
                <div className="glass rounded-2xl p-10 text-center">
                  <FolderOpen size={48} className="text-[#71717a] mx-auto mb-4" />
                  <p className="text-[#71717a] mb-4">Aucun projet pour le moment</p>
                  <button
                    onClick={() => setShowCreateProject(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#050505] rounded-lg font-medium hover:bg-[#F3E5AB] transition-colors"
                  >
                    <Plus size={18} />
                    Créer votre premier projet
                  </button>
                </div>
              ) : projects.length > 0 ? (
                projects.map((project, index) => {
                  const statusInfo = getStatusInfo(project.status || 'soumission');
                  const StatusIcon = statusInfo.icon;
                  const docCount = getProjectDocCount(project.id);
                  const adminDocCount = getAdminDocCount(project.id);
                  const hasTransferProof = (documents[project.id] || []).some(d => d.documentType === 'Preuve de virement');
                  const isInProduction = ['virement', 'production', 'diffusion'].includes(project.status);
                  
                  return (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="glass rounded-2xl overflow-hidden hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => navigate(`/projet/${token}`)}
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
                              <Film size={28} className="text-[#D4AF37]" />
                            </div>
                            <div>
                              <h3 className="text-lg font-medium text-white">{project.projectTitle}</h3>
                              <p className="text-[#71717a] text-sm">{project.category}</p>
                            </div>
                          </div>
                          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${statusInfo.color}/20`}>
                            <StatusIcon size={14} className={`text-${statusInfo.color.replace('bg-', '')}`} />
                            <span className="text-sm text-white">{statusInfo.label}</span>
                          </div>
                        </div>

                        {/* Budget & Transfer Info (only for production+ projects) */}
                        {isInProduction && (
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            {/* Budget Alloué */}
                            <div className="p-3 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                              <div className="flex items-center gap-2 mb-1">
                                <Banknote size={14} className="text-[#D4AF37]" />
                                <span className="text-[#D4AF37] text-xs font-medium">Budget alloué</span>
                              </div>
                              <p className="text-white font-semibold">
                                {project.allocatedBudget || 'En attente'}
                              </p>
                            </div>
                            
                            {/* Virement Status */}
                            <div className={`p-3 rounded-xl ${
                              project.status === 'virement' || project.status === 'diffusion'
                                ? 'bg-emerald-500/10 border border-emerald-500/30'
                                : 'bg-white/5 border border-white/10'
                            }`}>
                              <div className="flex items-center gap-2 mb-1">
                                <CreditCard size={14} className={
                                  project.status === 'virement' || project.status === 'diffusion'
                                    ? 'text-emerald-400'
                                    : 'text-[#71717a]'
                                } />
                                <span className={`text-xs font-medium ${
                                  project.status === 'virement' || project.status === 'diffusion'
                                    ? 'text-emerald-400'
                                    : 'text-[#71717a]'
                                }`}>Virement</span>
                              </div>
                              {project.status === 'virement' || project.status === 'diffusion' ? (
                                <p className="text-emerald-400 font-semibold flex items-center gap-1">
                                  <CheckCircle size={14} />
                                  Émis
                                </p>
                              ) : (
                                <p className="text-[#71717a]">En attente</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Mini Timeline */}
                        <div className="flex items-center gap-1 mb-4">
                          {statusSteps.map((step, stepIndex) => {
                            const currentIndex = getStatusIndex(project.status || 'soumission');
                            const isCompleted = stepIndex < currentIndex;
                            const isCurrent = stepIndex === currentIndex;
                            
                            return (
                              <div 
                                key={step.id}
                                className={`h-1 flex-1 rounded-full ${
                                  isCompleted ? 'bg-[#D4AF37]' : isCurrent ? 'bg-[#D4AF37]/50' : 'bg-white/10'
                                }`}
                              />
                            );
                          })}
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-4 text-sm flex-wrap">
                          <span className="flex items-center gap-1 text-[#71717a]">
                            <FileText size={14} />
                            {docCount} document(s)
                          </span>
                          {adminDocCount > 0 && (
                            <span className="flex items-center gap-1 text-green-400">
                              <CheckCircle size={14} />
                              {adminDocCount} de Second Souffle
                            </span>
                          )}
                          {hasTransferProof && (
                            <span className="flex items-center gap-1 text-emerald-400">
                              <CheckCircle size={14} />
                              Preuve de virement
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-[#D4AF37] ml-auto">
                            <Eye size={14} />
                            Voir détails
                          </span>
                        </div>

                        {/* Linked Producers */}
                        {project.linkedProducersInfo && project.linkedProducersInfo.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="flex items-center gap-2 mb-2">
                              <User size={14} className="text-blue-400" />
                              <span className="text-[#a1a1aa] text-sm">Producteurs associés</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {project.linkedProducersInfo.map((prod, idx) => (
                                <div 
                                  key={idx}
                                  className="px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-sm"
                                >
                                  <span className="text-white">{prod.name || prod.email}</span>
                                  {prod.company && (
                                    <span className="text-[#71717a] ml-1">• {prod.company}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })
              ) : null}
            </div>

            {/* Sidebar - Documents */}
            <div className="space-y-6">
              
              {/* Pitch Section - PRIMARY */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass rounded-2xl p-6 border border-[#D4AF37]/30"
              >
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <FileVideo size={18} className="text-[#D4AF37]" />
                  Pitch du projet
                </h3>
                
                {pitchDocument ? (
                  <div className="p-4 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-medium">{pitchDocument.fileName}</p>
                        <p className="text-[#D4AF37] text-xs">Pitch déposé</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownload(pitchDocument.id)}
                          className="p-2 rounded-lg bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] transition-colors"
                        >
                          <Download size={16} />
                        </button>
                        <CheckCircle size={18} className="text-green-400" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-2 border-dashed border-[#D4AF37]/30 rounded-xl text-center">
                    <FileVideo size={24} className="text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-[#a1a1aa] text-sm mb-3">
                      Déposez votre pitch (PDF, vidéo...)
                    </p>
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        className="hidden"
                        accept=".pdf,.mp4,.mov,.avi,.doc,.docx"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleDocumentUpload(file, 'Pitch');
                        }}
                        disabled={uploadingDoc === 'Pitch' || projects.length === 0}
                      />
                      <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] rounded-lg text-sm font-medium hover:bg-[#F3E5AB] transition-colors">
                        {uploadingDoc === 'Pitch' ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Envoi...
                          </>
                        ) : (
                          <>
                            <Upload size={14} />
                            Déposer mon pitch
                          </>
                        )}
                      </span>
                    </label>
                  </div>
                )}
              </motion.div>

              {/* KBIS Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass rounded-2xl p-6"
              >
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Building size={18} className="text-blue-400" />
                  KBIS / Statuts
                </h3>
                
                {kbisDocument ? (
                  <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-medium">{kbisDocument.fileName}</p>
                        <p className="text-blue-400 text-xs">KBIS déposé</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownload(kbisDocument.id)}
                          className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 transition-colors"
                        >
                          <Download size={16} />
                        </button>
                        <CheckCircle size={18} className="text-green-400" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-2 border-dashed border-blue-500/30 rounded-xl text-center">
                    <Building size={24} className="text-blue-400 mx-auto mb-2" />
                    <p className="text-[#a1a1aa] text-sm mb-3">
                      Extrait KBIS ou statuts de votre société
                    </p>
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleDocumentUpload(file, 'KBIS');
                        }}
                        disabled={uploadingDoc === 'KBIS' || projects.length === 0}
                      />
                      <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-400 transition-colors">
                        {uploadingDoc === 'KBIS' ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Envoi...
                          </>
                        ) : (
                          <>
                            <Upload size={14} />
                            Déposer KBIS
                          </>
                        )}
                      </span>
                    </label>
                  </div>
                )}
              </motion.div>

              {/* RIB Section - Less prominent */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass rounded-2xl p-6 opacity-90"
              >
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <CreditCard size={18} className="text-amber-400" />
                  RIB
                </h3>
                
                {ribDocument ? (
                  <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">{ribDocument.fileName}</p>
                        <p className="text-amber-400 text-xs">RIB déposé</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownload(ribDocument.id)}
                          className="p-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 transition-colors"
                        >
                          <Download size={14} />
                        </button>
                        <CheckCircle size={16} className="text-green-400" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 border border-dashed border-amber-500/30 rounded-xl text-center">
                    <p className="text-[#71717a] text-xs mb-2">
                      Pour recevoir vos paiements
                    </p>
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleDocumentUpload(file, 'RIB');
                        }}
                        disabled={uploadingDoc === 'RIB' || projects.length === 0}
                      />
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/80 text-[#050505] rounded-lg text-xs font-medium hover:bg-amber-400 transition-colors">
                        {uploadingDoc === 'RIB' ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <>
                            <Upload size={12} />
                            Déposer RIB
                          </>
                        )}
                      </span>
                    </label>
                  </div>
                )}
              </motion.div>

              {/* Contact */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="glass rounded-2xl p-6"
              >
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Mail size={18} className="text-[#D4AF37]" />
                  Contact
                </h3>
                <p className="text-[#a1a1aa] text-sm mb-4">
                  Une question sur vos projets ? Contactez notre équipe.
                </p>
                <a 
                  href="mailto:contact@secondsouffleprod.com"
                  className="block w-full text-center px-4 py-3 bg-[#D4AF37] text-[#050505] rounded-lg font-medium hover:bg-[#F3E5AB] transition-colors"
                >
                  Contacter l'équipe
                </a>
              </motion.div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProducerSpace;
